import React, { useEffect } from 'react';
import useSharedLeaveService from 'domain/shared/leave/leaveService';
import { formatLeaveTypeWithSubType } from 'domain/shared/leave/leaveTypeDisplay';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { FormFieldsData, useFormContext, useInputHelper, LoadDropdown } from 'framework/form';

export interface LeaveDropdownProps {
  source: string;
  label: string;
  isVisible?: boolean | ((data: FormFieldsData) => boolean);
  checkDepending?: boolean;
  deps?: any[];
  filter?: Dictionary<any>;
  loadDataMethod?: (params: any) => Promise<any>;
  props?: any;
}

const LeaveDropdown = ({ checkDepending, deps = [], filter, loadDataMethod, ...props }: LeaveDropdownProps) => {
  const { searchAll } = useSharedLeaveService();
  const { isDirty } = useFormContext();
  const { formatDate } = useDateService();
  const { getSource, getRawOnChangeCallback } = useInputHelper(props);
  const source = getSource();
  const parentOnChange = getRawOnChangeCallback() || (() => {});

  const loadLeaves = async () => {
    if (checkDepending && deps.some(dep => !dep)) {
      return Promise.resolve({ result: [] });
    }

    const files = await (loadDataMethod || searchAll)({
      filter,
      sort: { startDate: 'DESC' },
    });

    return Promise.resolve({ result: files });
  };

  useEffect(() => {
    if (isDirty && checkDepending) {
      parentOnChange({
        [source]: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const mapResult = (item: any) => {
    const start = item.period?.startDate ?? item.startDate;
    const end = item.period?.plannedEndDate ?? item.period?.actualEndDate ?? item.endDate;
    const typeLabel = formatLeaveTypeWithSubType({
      type: item.type,
      subType: item.subType,
      sickDayReason: item.sickDayReason,
    });
    return {
      value: item.id,
      label: `${typeLabel}, ${formatDate(start, DATE_FORMAT.FULL_DATE)?.trim()} - ${formatDate(
        end,
        DATE_FORMAT.FULL_DATE,
      )?.trim()}`,
    };
  };

  return (
    <LoadDropdown
      {...props}
      loadDataMethod={loadLeaves}
      mapResults={mapResult}
      deps={deps}
      showLoader
      placeholder="Select Leave"
    />
  );
};

export default LeaveDropdown;
